/**
 * CommunityOpportunitiesPage — §3 Volunteer Opportunities.
 * A unified, joinable feed of upcoming community events + active community
 * campaigns, presented as OpportunityCards (Title, Description, Location/Online,
 * Date, Time, Skills [events], Organizer, Join). Leaders/coordinators get a
 * "Create event" control (reuses createEvent). No financial fields anywhere.
 */
import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Compass, Plus, X, CalendarDays, Clock, MapPin, Users, Sparkles } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { SectionLoading, StatusPill } from '@/components/community/ui'
import {
  listEvents, registerForEvent, cancelEventRegistration, createEvent,
  listCommunityCampaigns, joinCommunityCampaign,
  type EventResponse, type CommunityCampaignResponse, type EventCreateRequest,
} from '@/services/communityApi'
import { formatDate } from '@/lib/utils'

const EVENT_TYPES = ['AWARENESS_SESSION', 'WORKSHOP', 'DIGITAL_DRIVE', 'COLLEGE_ACTIVITY', 'PUBLIC_EVENT'] as const

/** Format just the time-of-day from an ISO datetime ("7:30 PM"). */
function formatTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch { return '' }
}

type Opportunity =
  | { kind: 'event'; data: EventResponse }
  | { kind: 'campaign'; data: CommunityCampaignResponse }

export default function CommunityOpportunitiesPage() {
  const { t } = useTranslation()
  const { community, canManage } = useCommunity()
  const [events, setEvents] = useState<EventResponse[]>([])
  const [campaigns, setCampaigns] = useState<CommunityCampaignResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const load = useCallback(() => {
    if (!community) return
    setLoading(true)
    Promise.allSettled([
      listEvents(community.id, 'UPCOMING', 0, 50),
      listCommunityCampaigns(community.id),
    ]).then(([ev, ca]) => {
      if (ev.status === 'fulfilled') setEvents(ev.value.content)
      if (ca.status === 'fulfilled') setCampaigns(ca.value)
      setLoading(false)
    })
  }, [community])

  useEffect(() => { load() }, [load])

  const onRegister = async (e: EventResponse) => {
    setBusyId(`e${e.id}`)
    try { const upd = await registerForEvent(e.id); setEvents((p) => p.map((x) => x.id === upd.id ? upd : x)) } catch { /* ignore */ }
    finally { setBusyId(null) }
  }
  const onCancelEvent = async (e: EventResponse) => {
    setBusyId(`e${e.id}`)
    try { const upd = await cancelEventRegistration(e.id); setEvents((p) => p.map((x) => x.id === upd.id ? upd : x)) } catch { /* ignore */ }
    finally { setBusyId(null) }
  }
  const onJoinCampaign = async (c: CommunityCampaignResponse) => {
    setBusyId(`c${c.id}`)
    try { const upd = await joinCommunityCampaign(c.id); setCampaigns((p) => p.map((x) => x.id === upd.id ? upd : x)) } catch { /* ignore */ }
    finally { setBusyId(null) }
  }

  // Render the shell even while the community is still loading (avoids a blank
  // flash on the /events → /opportunities redirect and gives the probe a stable
  // shell to assert against). The fetch itself no-ops until community resolves.
  const opportunities: Opportunity[] = [
    ...events.map((e) => ({ kind: 'event' as const, data: e })),
    ...campaigns.filter((c) => c.status === 'ACTIVE' || c.status === 'UPCOMING').map((c) => ({ kind: 'campaign' as const, data: c })),
  ]

  return (
    <CommunityLayout>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <GovPageTitle title={t('community.opportunities.title')} subtitle={t('community.opportunities.subtitle')} icon={Compass} />
        {canManage && (
          <GovButton variant="primary" size="sm" onClick={() => setShowCreate(true)} className="mt-1">
            <Plus className="w-4 h-4 mr-1" aria-hidden="true" />{t('community.opportunities.createEvent')}
          </GovButton>
        )}
      </div>

      {loading ? <SectionLoading rows={3} /> : opportunities.length === 0 ? (
        <GovEmptyState icon="🧭" title={t('community.opportunities.none')} description={t('community.opportunities.noneHint')} />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {opportunities.map((op) => op.kind === 'event'
            ? <OpportunityEventCard key={`e${op.data.id}`} event={op.data} onRegister={onRegister} onCancel={onCancelEvent} busy={busyId === `e${op.data.id}`} />
            : <OpportunityCampaignCard key={`c${op.data.id}`} campaign={op.data} onJoin={onJoinCampaign} busy={busyId === `c${op.data.id}`} />
          )}
        </div>
      )}

      {showCreate && <CreateEventModal onClose={() => setShowCreate(false)} onCreated={(ev) => { setEvents((p) => [ev, ...p]); setShowCreate(false) }} />}
    </CommunityLayout>
  )
}

function MetaLine({ icon: Icon, children }: { icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1 text-xs text-gray-500">
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />{children}
    </span>
  )
}

function OpportunityEventCard({ event, onRegister, onCancel, busy }: {
  event: EventResponse; onRegister: (e: EventResponse) => void; onCancel: (e: EventResponse) => void; busy: boolean
}) {
  const { t } = useTranslation()
  const full = event.capacity > 0 && event.registeredCount >= event.capacity
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
          <CalendarDays className="w-5 h-5 text-blue-600" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 truncate">{event.title}</h3>
            <StatusPill status={event.status} />
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{t('community.opportunities.tagEvent')}</span>
          </div>
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{event.description}</p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <MetaLine icon={CalendarDays}>{formatDate(event.startDate)}</MetaLine>
            <MetaLine icon={Clock}>{formatTime(event.startDate)}</MetaLine>
            {event.location && <MetaLine icon={MapPin}>{event.location}</MetaLine>}
            <MetaLine icon={Users}>{event.organizer?.fullName}</MetaLine>
            <MetaLine icon={Users}>{event.registeredCount}/{event.capacity || '∞'}</MetaLine>
          </div>
          {event.skills && event.skills.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              <Sparkles className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
              {event.skills.map((s) => (
                <span key={s} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{s}</span>
              ))}
            </div>
          )}
          <div className="mt-3">
            {event.myRegistered ? (
              <button onClick={() => onCancel(event)} disabled={busy}
                className="text-sm font-medium text-red-600 hover:underline disabled:opacity-50 min-h-[40px]">
                {t('community.actions.cancelRegistration')}
              </button>
            ) : (
              <button onClick={() => onRegister(event)} disabled={busy || full}
                className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">
                {full ? t('community.events.capacity') : t('community.actions.join')}
              </button>
            )}
          </div>
        </div>
      </div>
    </GovCard>
  )
}

function OpportunityCampaignCard({ campaign, onJoin, busy }: {
  campaign: CommunityCampaignResponse; onJoin: (c: CommunityCampaignResponse) => void; busy: boolean
}) {
  const { t } = useTranslation()
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
          <Compass className="w-5 h-5 text-emerald-600" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 truncate">{campaign.title}</h3>
            <StatusPill status={campaign.status} />
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{t('community.opportunities.tagCampaign')}</span>
          </div>
          {campaign.objective && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{campaign.objective}</p>}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {campaign.startDate && <MetaLine icon={CalendarDays}>{formatDate(campaign.startDate)}</MetaLine>}
            {campaign.location && <MetaLine icon={MapPin}>{campaign.location}</MetaLine>}
            {campaign.organizerName && <MetaLine icon={Users}>{campaign.organizerName}</MetaLine>}
            {campaign.volunteersNeeded != null && <MetaLine icon={Users}>{t('community.campaigns.volunteersNeeded')}: {campaign.volunteersNeeded}</MetaLine>}
          </div>
          <div className="mt-3">
            <button onClick={() => onJoin(campaign)} disabled={busy || campaign.myParticipation}
              className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">
              {campaign.myParticipation ? t('community.campaigns.joined') : t('community.actions.join')}
            </button>
          </div>
        </div>
      </div>
    </GovCard>
  )
}

/** Inline create-event modal (leaders/coordinators) — reuses createEvent. */
function CreateEventModal({ onClose, onCreated }: { onClose: () => void; onCreated: (e: EventResponse) => void }) {
  const { t } = useTranslation()
  const [form, setForm] = useState<EventCreateRequest>({ title: '', type: 'AWARENESS_SESSION', location: '', capacity: 50, description: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true); setError(null)
    try { const created = await createEvent(form); onCreated(created) }
    catch (err: any) { setError(err?.response?.data?.message || t('errors.generic')) }
    finally { setSaving(false) }
  }
  const field = (k: keyof EventCreateRequest, v: string | number | string[]) => setForm((p) => ({ ...p, [k]: v }))
  const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('community.opportunities.createEvent')}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <GovCard className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('community.opportunities.createEvent')}</h2>
          <button onClick={onClose} aria-label={t('community.actions.cancel')} className="p-2 rounded-lg hover:bg-gray-100 min-h-[40px] min-w-[40px] flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.profile.name')}</label>
            <input required value={form.title} onChange={(e) => field('title', e.target.value)} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.events.typeLabel')}</label>
              <select value={form.type} onChange={(e) => field('type', e.target.value)} className={inputCls}>
                {EVENT_TYPES.map((tp) => <option key={tp} value={tp}>{t(`community.events.type.${tp}`, tp)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.events.capacity')}</label>
              <input type="number" min={0} value={form.capacity ?? ''} onChange={(e) => field('capacity', Number(e.target.value))} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.events.date')} (start)</label>
              <input type="datetime-local" value={form.startDate ?? ''} onChange={(e) => field('startDate', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.events.date')} (end)</label>
              <input type="datetime-local" value={form.endDate ?? ''} onChange={(e) => field('endDate', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.events.location')}</label>
            <input value={form.location ?? ''} onChange={(e) => field('location', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.opportunities.skills')}</label>
            <input value={(form.skills ?? []).join(', ')} onChange={(e) => field('skills', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              placeholder={t('community.opportunities.skillsHint')} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.profile.description')}</label>
            <textarea value={form.description ?? ''} onChange={(e) => field('description', e.target.value)} rows={3} className={inputCls} />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <GovButton variant="secondary" size="sm" onClick={onClose}>{t('community.actions.cancel')}</GovButton>
            <GovButton variant="primary" size="sm" type="submit" disabled={saving}>{saving ? t('common.saving') : t('community.actions.create')}</GovButton>
          </div>
        </form>
      </GovCard>
    </div>
  )
}